"use client"

import * as React from "react"
import * as AvatarPrimitive from "@radix-ui/react-avatar"
import { Pencil } from "lucide-react"

import { cn } from "../../../utils/tailwind-utils"

export interface AvatarProps extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> {
  /** Size of the avatar - small (48x48) or large (72x72) */
  size?: 'sm' | 'lg';
  /** Whether to show the edit button */
  showEditButton?: boolean;
  /** Function to call when edit button is clicked */
  onEditClick?: () => void;
}

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  AvatarProps
>(({ className, size = 'sm', showEditButton = false, onEditClick, ...props }, ref) => {
  // Size classes based on requirements
  const sizeClasses = {
    sm: 'h-[48px] w-[48px]', // 48x48px
    lg: 'h-[72px] w-[72px]' // 72x72px
  };

  return (
    <div className="relative inline-block">
      <AvatarPrimitive.Root
        ref={ref}
        className={cn(
          "relative flex shrink-0 overflow-hidden rounded-full",
          sizeClasses[size],
          className
        )}
        {...props}
      />
      {showEditButton && (
        <button 
          onClick={onEditClick} 
          className={cn(
            "absolute bottom-0 right-0 bg-white rounded-full shadow-md hover:bg-neutral-10 focus:outline-none focus:ring-2 focus:ring-primary-indigo-500",
            size === 'sm' ? 'p-0.5' : 'p-1.5'
          )}
          aria-label="Edit avatar"
        >
          <Pencil className={cn(
            "text-primary-indigo-500",
            size === 'sm' ? 'h-2.5 w-2.5' : 'h-3 w-3'
          )} />
        </button>
      )}
    </div>
  );
});
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn("aspect-square h-full w-full object-cover", className)}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      "flex h-full w-full items-center justify-center rounded-full bg-primary-indigo-100 text-primary-indigo-700 font-medium",
      className
    )}
    {...props}
  />
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

export { Avatar, AvatarImage, AvatarFallback }
